/**
 * Minimal Google Tag Manager bootstrap utilities.
 *
 * Important:
 * - Push any required context to the dataLayer BEFORE calling initGtm()
 *   so that tags firing on the initial `event: 'gtm.js'` can access it.
 */
const DEFAULT_DATALAYER_NAME = 'dataLayer';
const DEFAULT_GTM_SCRIPT_ID = 'gtm-js';

function getWindow() {
  if (typeof window === 'undefined') return null;
  return window;
}

export function ensureDataLayer(layerName = DEFAULT_DATALAYER_NAME) {
  const w = getWindow();
  if (!w) return [];

  w[layerName] = w[layerName] || [];
  return w[layerName];
}

export function pushToDataLayer(payload, layerName = DEFAULT_DATALAYER_NAME) {
  if (!payload || typeof payload !== 'object') return;
  const dl = ensureDataLayer(layerName);
  dl.push(payload);
}

export function isGtmLoaded(gtmScriptId = DEFAULT_GTM_SCRIPT_ID) {
  const w = getWindow();
  if (!w) return false;

  if (w.google_tag_manager) return true;
  if (typeof document === 'undefined') return false;
  return Boolean(document.getElementById(gtmScriptId));
}

export function loadGtmScript({
  gtmId,
  layerName = DEFAULT_DATALAYER_NAME,
  gtmScriptId = DEFAULT_GTM_SCRIPT_ID,
} = {}) {
  const w = getWindow();
  if (!w) return;
  if (!gtmId) return;
  if (typeof document === 'undefined') return;
  if (document.getElementById(gtmScriptId)) return;

  const firstScript = document.getElementsByTagName('script')[0];
  const script = document.createElement('script');
  const dlParam = layerName !== DEFAULT_DATALAYER_NAME ? `&l=${layerName}` : '';

  script.id = gtmScriptId;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}${dlParam}`;

  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
}

/**
 * Initializes GTM by pushing the `gtm.js` event and injecting the GTM script.
 * Callers should push any required user context before calling this.
 */
export function initGtm({
  gtmId,
  layerName = DEFAULT_DATALAYER_NAME,
  gtmScriptId = DEFAULT_GTM_SCRIPT_ID,
} = {}) {
  if (!gtmId) return;

  // Ensure dataLayer exists before any push.
  ensureDataLayer(layerName);

  // If already loaded/inserted, do not push another initial gtm.js event.
  if (isGtmLoaded(gtmScriptId)) return;

  // Standard snippet behavior: queue the initial gtm.js event BEFORE loading gtm.js.
  pushToDataLayer(
    {
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    },
    layerName
  );

  loadGtmScript({ gtmId, layerName, gtmScriptId });
}
